import { useEffect, useState, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './CoachingBrief.css';
import '../pages/Landing.css';

/* ====================================================================
   Types
   ==================================================================== */

type GoalStatus = 'on_track' | 'needs_attention' | 'great_progress';
type SectionKey = 'diet' | 'exercise' | 'mind' | 'general';

interface CoachingSection {
  key: SectionKey;
  title: string;
  body: string;
  tip: string | null;
  badges: string[];
}

interface CoachingGoal {
  id: string;
  title: string;
  category: string;
  note: string;
  status: GoalStatus;
}

type ScheduleItemType = 'habit' | 'workout' | 'shopping' | 'cooking';

interface ScheduleItem {
  type: ScheduleItemType;
  title: string;
  time?: string;
  detail?: string;
}

interface SummaryData {
  overview: string;
  sections: CoachingSection[];
  goals: CoachingGoal[];
  schedule?: ScheduleItem[];
  focus_for_today: string;
  encouragement: string | null;
}

interface CoachingBriefData {
  nanoid: string;
  applies_date: string;
  analyzed_date: string;
  summary_data: SummaryData;
  generated_at: string;
  first_name: string | null;
}

/* ====================================================================
   Helpers
   ==================================================================== */

function formatDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

function capitalize(str: string): string {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatBadge(badge: string): string {
  return badge.split('-').map(capitalize).join(' ');
}

function greeting(firstName: string | null): string {
  const hour = new Date().getHours();
  const time = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  return firstName ? `${time}, ${firstName}!` : `${time}!`;
}

const SECTION_EMOJI: Record<SectionKey, string> = {
  diet: '🥗',
  exercise: '💪',
  mind: '🧘',
  general: '✨',
};

const SCHEDULE_EMOJI: Record<ScheduleItemType, string> = {
  habit: '🔁',
  workout: '💪',
  shopping: '🛒',
  cooking: '🍳',
};

/** Formats a 'HH:MM' 24h time string into a friendly '7:00 AM'. Returns '' if unparseable. */
function formatScheduleTime(time?: string): string {
  if (!time) return '';
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const period = h < 12 ? 'AM' : 'PM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

/* ====================================================================
   Shell
   ==================================================================== */

function CoachingShell({ children }: { children: ReactNode }) {
  return (
    <div className="coaching-page">
      <PublicHeader />
      {children}
      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Page
   ==================================================================== */

export function CoachingBriefPage() {
  const { nanoid } = useParams<{ nanoid: string }>();

  const [brief, setBrief] = useState<CoachingBriefData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not_found' | 'expired' | 'error' | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [nanoid]);

  useEffect(() => {
    if (!nanoid) {
      setError('not_found');
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        const res = await fetch(`/api/coaching/${nanoid}`);
        if (cancelled) return;
        if (res.status === 404) { setError('not_found'); setLoading(false); return; }
        if (res.status === 410) { setError('expired');   setLoading(false); return; }
        if (!res.ok) { setError('error'); setLoading(false); return; }
        const json = await res.json();
        setBrief(json.data);
        setLoading(false);
      } catch {
        if (!cancelled) { setError('error'); setLoading(false); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [nanoid]);

  if (loading) {
    return (
      <CoachingShell>
        <div className="coaching-loading">
          <div className="coaching-loading-spinner" />
          <p>Loading your coaching brief…</p>
        </div>
      </CoachingShell>
    );
  }

  if (error || !brief) {
    return (
      <CoachingShell>
        <div className="coaching-not-found">
          <div className="coaching-not-found-icon">🌿</div>
          <h2>Brief not available</h2>
          <p>
            {error === 'expired'
              ? 'This link has expired. Text Mosaime to get your latest coaching brief.'
              : error === 'not_found'
              ? 'This coaching brief is no longer available. Open Mosaime to see your latest insights.'
              : "We couldn't load this brief. Please try again."}
          </p>
          <Link to="/" className="btn hero-cta">Go to Mosaime</Link>
        </div>
      </CoachingShell>
    );
  }

  const { applies_date, first_name, summary_data } = brief;

  return (
    <CoachingShell>

      {/* Hero */}
      <header className="coaching-hero">
        <div className="landing-container coaching-hero-inner">
          <p className="coaching-period-label">Daily Coaching Brief</p>
          <h1 className="coaching-title">Your Coaching Brief — {formatDate(applies_date)}</h1>
          <p className="coaching-greeting">{greeting(first_name)}</p>
        </div>
      </header>

      <div className="landing-container coaching-body">

        {/* Overview */}
        <section className="coaching-overview">
          <p className="coaching-overview-text">{summary_data.overview}</p>
        </section>

        {/* Today's Agenda */}
        {summary_data.schedule && summary_data.schedule.length > 0 && (
          <section className="coaching-section">
            <h2 className="coaching-section-title">
              <span aria-hidden="true">🗓️</span> Today's Agenda
            </h2>
            <div className="coaching-agenda-list">
              {summary_data.schedule.map((item, i) => (
                <div key={i} className="coaching-agenda-item">
                  <span className="coaching-agenda-emoji" aria-hidden="true">
                    {SCHEDULE_EMOJI[item.type] ?? '•'}
                  </span>
                  <div className="coaching-agenda-content">
                    <span className="coaching-agenda-title">{item.title}</span>
                    {item.detail && <span className="coaching-agenda-detail">{item.detail}</span>}
                  </div>
                  {item.time && (
                    <span className="coaching-agenda-time">{formatScheduleTime(item.time)}</span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Sections */}
        {summary_data.sections.length > 0 && (
          <section className="coaching-section">
            <h2 className="coaching-section-title">
              <span aria-hidden="true">📋</span> Today's Breakdown
            </h2>
            <div className="coaching-cards">
              {summary_data.sections.map((sec) => (
                <div key={sec.key} className="coaching-card">
                  <div className="coaching-card-header">
                    <span className="coaching-card-emoji" aria-hidden="true">
                      {SECTION_EMOJI[sec.key] ?? '✨'}
                    </span>
                    <span className="coaching-card-label">{sec.title}</span>
                  </div>
                  <p className="coaching-card-body">{sec.body}</p>
                  {sec.tip && (
                    <div className="coaching-tip">
                      <span className="coaching-tip-icon" aria-hidden="true">💡</span>
                      <p className="coaching-tip-text">{sec.tip}</p>
                    </div>
                  )}
                  {sec.badges.length > 0 && (
                    <div className="coaching-badges">
                      {sec.badges.map((badge, i) => (
                        <span key={i} className="coaching-badge">{formatBadge(badge)}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Goals */}
        {summary_data.goals.length > 0 && (
          <section className="coaching-section">
            <h2 className="coaching-section-title">
              <span aria-hidden="true">🎯</span> Your Goals
            </h2>
            <div className="coaching-goal-list">
              {summary_data.goals.map((goal) => (
                <div key={goal.id} className="coaching-goal-item">
                  <div className="coaching-goal-header">
                    <span
                      className={`coaching-goal-dot coaching-goal-dot--${goal.status}`}
                      aria-label={goal.status.replace(/_/g, ' ')}
                    />
                    <span className="coaching-goal-title">{goal.title}</span>
                  </div>
                  {goal.note && <p className="coaching-goal-note">{goal.note}</p>}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Focus for Today */}
        <div className="coaching-focus">
          <p className="coaching-focus-label">Focus for Today</p>
          <p className="coaching-focus-text">{summary_data.focus_for_today}</p>
        </div>

        {/* Encouragement */}
        {summary_data.encouragement && (
          <section className="coaching-encouragement">
            <p className="coaching-encouragement-text">{summary_data.encouragement}</p>
            <p className="coaching-encouragement-cta">Reply to your SMS anytime to check in</p>
          </section>
        )}

      </div>
    </CoachingShell>
  );
}

export default CoachingBriefPage;
